import type { ColorMode } from '@chukua/common';
import type { ContainerLivery, RigVehicles, ShowcaseRig } from '@chukua/vehicle-model/showcase';
import clsx from 'clsx';
import { lazy, Suspense, type RefObject } from 'react';
import { useRigShowcase } from '../hooks/use-rig-showcase';

export type { ContainerLivery, RigVehicles } from '@chukua/vehicle-model/showcase';

const VehicleShowcase = lazy(() => import('../components/VehicleShowcase').then((module) => ({ default: module.VehicleShowcase })));

/** A 6x4 tractor with a 40ft container on a skeletal: the rig a port haulier runs most, with a side wide enough for a logo. */
const CONTAINER_RIG: ShowcaseRig = { trailer: 'skeletal_40' };

interface RigShowcaseProps {
  /** The company painted on the container. */
  livery: ContainerLivery;
  /** The fleet vehicles the rigs show, plate and paint: the first for the company's rig, the rest for the others. Null while they load: the 3D waits, so the rigs are built once. */
  vehicles: RigVehicles[] | null;
  /** The company's country outline (`/country-outlines/<code>.json`, `make country-outline`), drawn on the floor; null for the plain grid. */
  countryOutlineSrc: string | null;
  /** The surface's colour mode: the floor's lines and the shadow follow it. */
  mode: ColorMode;
  /** The rig rendered plain at the opening shot for this mode (`make login-rig`): shown in the frame at once, and kept where there's no WebGL. */
  stillSrc: string;
  /** A 16:10 box in the surface's layout where the rig stands. The showcase itself fills the surface behind it, so the floor can run back toward the horizon. */
  frameRef: RefObject<HTMLElement | null>;
  className?: string;
}

/**
 * A container rig carrying the company's logo, plates and paint on a grid floor with its country and its other container rigs, the
 * camera flying a slow loop round it, for a brand surface in either colour mode. Place it as an
 * absolutely positioned layer over the whole surface, behind its content. Props only: no fetch of
 * its own data, router or organization context. The still shows first and the 3D scene fades in over it once drawn.
 */
export function RigShowcase({ livery, vehicles, countryOutlineSrc, mode, stillSrc, frameRef, className }: RigShowcaseProps) {
  const { layerRef, layout, motion, is3dDrawn, mark3dDrawn } = useRigShowcase(frameRef);
  return (
    <div ref={layerRef} aria-hidden className={clsx('pointer-events-none absolute inset-0', className)}>
      {layout && (
        <img
          src={stillSrc}
          alt=""
          className={clsx('absolute max-w-none transition-opacity duration-1000', is3dDrawn && 'opacity-0')}
          style={{ left: layout.frame.x, top: layout.frame.y, width: layout.frame.width, height: layout.frame.height }}
        />
      )}
      {layout && motion && vehicles && (
        <Suspense fallback={null}>
          <VehicleShowcase
            rig={CONTAINER_RIG}
            livery={livery}
            vehicles={vehicles}
            countryOutlineSrc={countryOutlineSrc}
            motion={motion}
            mode={mode}
            layout={layout}
            onDrawn={mark3dDrawn}
          />
        </Suspense>
      )}
    </div>
  );
}
